import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDatabase,
    faBuilding,
    faClock,
    faCheckCircle,
    faSearch,
    faInfoCircle,
    faTimes,
    faSort,
    faCalendarAlt,
    faArrowTrendUp,
    faArrowTrendDown,
    faUsers,
    faLock,
    faSliders,
    faChevronDown
} from "@fortawesome/free-solid-svg-icons";
import PaidSponsorTable from "../../components/PaidSponsorTable";
import axios from 'axios';
import config from '../../config';
import { loadStripe } from '@stripe/stripe-js';
import '../../css/Sponsors.css';
import { trackSponsorsTabViewed } from '../../utils/funnelTracking';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useNiches } from '../../hooks/useNiches';
import { getNicheIcon } from '../../utils/nicheIcons';

interface SponsorsProps {
    sponsors: number;
    newsletters: number;
    lastUpdated: string;
    isSubscribed: boolean | string | null;
    user?: {
        email: string;
        name?: string;
        isAdmin?: boolean;
        newsletterInfo?: {
            name?: string;
            topic?: string;
            audience_size?: number;
            engagement_rate?: number;
            publishing_frequency?: string;
            audience_demographics?: {
                age_range?: string;
                income_range?: string;
                location?: string;
                interests?: string[];
                job_titles?: string[];
            };
        } | null;
    };
}

const stripeAPIKey = "pk_live_51MpGntBKPgChhmNg9wLgFqQICAjXSVAzaEMRKwXjuLQeZZhwghaiA7VDoG0Cov9uEnDGF9RlAKQkQ1xXPSooAX8D00Mp9uCFyO";
const stripePromise = loadStripe(stripeAPIKey);

const handlePurchase = async () => {
    try {
        const token = localStorage.getItem('token');
        if (!token) {
            return;
        }
        const response = await axios.post(`${config.backendUrl}users/checkout`, { plan: 'pro' },
            {
                headers: {
                    'x-auth-token': token
                }
            });

        const sessionId = response.data.sessionId;
        const stripe = await stripePromise;
        await stripe?.redirectToCheckout({
            sessionId: sessionId
        });

    } catch (error) {
        // Error subscribing handled silently
    }
}

// Status filter removed from user-facing UI (internal use only)

const Sponsors = ({ sponsors, newsletters, lastUpdated, isSubscribed, user }: SponsorsProps) => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [error, setError] = useState<string | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [activeFilters, setActiveFilters] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [audienceFilter, setAudienceFilter] = useState('any');
    const [sortBy, setSortBy] = useState('dateAdded');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [showAffiliatePrograms, setShowAffiliatePrograms] = useState(false);
    // Default to Best Matches only for users who've already completed
    // newsletter onboarding (same "topic" check used everywhere else this is
    // gated — see hasOnboarding below) — otherwise there's nothing to show
    // there yet, so default to the list that actually has content.
    const [activeTab, setActiveTab] = useState<'all' | 'matched'>(
        () => (user?.newsletterInfo?.topic ? 'matched' : 'all')
    );
    const [matchedSponsors, setMatchedSponsors] = useState<any[]>([]);
    const [loadingMatches, setLoadingMatches] = useState(false);
    // Turns "here's a database, good luck" into a worked pipeline — a
    // strong-match filter is only meaningful once qualitative labels exist
    // (see getMatchTier in PaidSponsorTable.tsx; kept in sync with the same
    // >=60 threshold here).
    const [matchFilter, setMatchFilter] = useState<'all' | 'strong' | 'recent' | 'similar-audience' | 'new'>('all');
    const [hasOnboarding, setHasOnboarding] = useState(false);
    const [showLockedTabPrompt, setShowLockedTabPrompt] = useState(false);
    // Niche chips default collapsed on mobile — 20+ real niches as a fixed
    // 2-column grid pushed search results down a full screen or more before
    // a visitor saw anything. Desktop ignores this (CSS always shows the
    // grid there); auto-opens if a filter is already active so a niche
    // link or restored selection is never hidden behind a closed toggle.
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    // Status filter not exposed to users; filtering enforced in table
    const { niches: allNiches } = useNiches();
    // "Affiliate" as a niche filter chip duplicates the dedicated "Show
    // Affiliate Programs" toggle below — dropped here only (the shared
    // niche list itself, and every other consumer of it, is untouched).
    const niches = allNiches.filter((n) => n.tag !== 'Affiliate');

    // Rate guidance ("Newsletters your size...") is genuinely useful once,
    // but showing it unconditionally on every visit above the grid reads as
    // nagging rather than helpful. Dismissing it is remembered per account
    // (keyed by email, since this component doesn't receive a user id) —
    // once someone's seen it and closed it, it stays closed for them.
    const rateGuidanceStorageKey = user?.email ? `rateGuidanceDismissed:${user.email}` : null;
    const [rateGuidanceDismissed, setRateGuidanceDismissed] = useState(
        () => !!rateGuidanceStorageKey && localStorage.getItem(rateGuidanceStorageKey) === 'true'
    );
    const dismissRateGuidance = () => {
        if (rateGuidanceStorageKey) localStorage.setItem(rateGuidanceStorageKey, 'true');
        setRateGuidanceDismissed(true);
    };

    useEffect(() => {
        if (activeFilters.length > 0) setFiltersExpanded(true);
    }, [activeFilters.length]);

    // A subscriber (or admin) clicking a niche link (homepage picker, footer,
    // or a /niche/:tag landing page) gets redirected here with ?niche=X —
    // apply it as a real filter once we know it's a real niche, then drop
    // it from the URL so it behaves like a normal filter click afterward.
    useEffect(() => {
        const requestedNiche = searchParams.get('niche');
        if (!requestedNiche || niches.length === 0) return;
        const match = niches.find(n => n.tag.toLowerCase() === requestedNiche.toLowerCase());
        if (match) {
            setActiveFilters(prev => prev.includes(match.tag) ? prev : [...prev, match.tag]);
        }
        setSearchParams(params => {
            params.delete('niche');
            return params;
        }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [niches]);

    // Set admin status from user prop
    useEffect(() => {
        setIsAdmin(user?.isAdmin === true);
    }, [user]);
    
    // Convert subscription to boolean for backward compatibility
    const hasSubscription = Boolean(isSubscribed);
    // Allow admins to access even without subscription
    const canAccess = hasSubscription || user?.isAdmin === true;

    // CRITICAL: Redirect non-subscribed, non-admin users to /subscribe
    useEffect(() => {
        if (!canAccess) {
            navigate('/subscribe', { replace: true });
        }
    }, [canAccess, navigate]);
    
    // Track sponsors tab viewed (funnel tracking)
    useEffect(() => {
        if (canAccess) {
            trackSponsorsTabViewed();
        }
    }, [canAccess]);
    
    // Check if user has completed onboarding
    useEffect(() => {
        const hasTopic = user?.newsletterInfo?.topic && user.newsletterInfo.topic.length > 0;
        setHasOnboarding(hasTopic || false);
    }, [user]);
    
    const fetchMatchedSponsors = useCallback(async () => {
        try {
            setLoadingMatches(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.backendUrl}sponsors/matched`, {
                headers: { 'x-auth-token': token }
            });
            
            if (response.data.success) {
                setMatchedSponsors(response.data.sponsors || []);
            }
        } catch (error: any) {
            console.error('Error fetching matched sponsors:', error);
            setError('Failed to load matched sponsors');
        } finally {
            setLoadingMatches(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'matched' && hasOnboarding && matchedSponsors.length === 0) {
            fetchMatchedSponsors();
        }
    }, [activeTab, hasOnboarding, matchedSponsors.length, fetchMatchedSponsors]);

    const isRecentlyActive = (sponsor: any) => {
        const date = sponsor.dateSponsored || sponsor.mostRecentNewsletterDate;
        if (!date) return false;
        return (Date.now() - new Date(date).getTime()) <= 30 * 24 * 60 * 60 * 1000;
    };
    const isNewToDatabase = (sponsor: any) => {
        if (!sponsor.dateAdded) return false;
        return (Date.now() - new Date(sponsor.dateAdded).getTime()) <= 7 * 24 * 60 * 60 * 1000;
    };

    // "227 applications" isn't the same thing as "227 good opportunities" —
    // this line exists to give a reason to keep going (real signal, not
    // just a raw count) without requiring anyone to scroll and count for
    // themselves.
    const matchSummary = useMemo(() => ({
        strong: matchedSponsors.filter((s) => s.matchScore >= 60).length,
        recentlyActive: matchedSponsors.filter(isRecentlyActive).length,
    }), [matchedSponsors]);

    const filteredMatchedSponsors = useMemo(() => {
        switch (matchFilter) {
            case 'strong': return matchedSponsors.filter((s) => s.matchScore >= 60);
            case 'recent': return matchedSponsors.filter(isRecentlyActive);
            case 'similar-audience': return matchedSponsors.filter((s) => s.audienceFit);
            case 'new': return matchedSponsors.filter(isNewToDatabase);
            default: return matchedSponsors;
        }
    }, [matchedSponsors, matchFilter]);


    // Enhanced filter handling with multi-selection
    const handleFilterToggle = (filterKey: string) => {
        setActiveFilters(prev => {
            if (prev.includes(filterKey)) {
                return prev.filter(f => f !== filterKey);
            } else {
                return [...prev, filterKey];
            }
        });
    };

    const clearAllFilters = () => {
        setActiveFilters([]);
    };

    const isFilterActive = (filterKey: string) => {
        return activeFilters.includes(filterKey);
    };

    // Enhanced sorting
    const handleSort = (field: string) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };



    if (canAccess) {
        return (
            <div className="page-shell page-shell--wide sponsors-page">
                <div className="sponsors-page-container">
                    <header className="page-header sponsors-page-header">
                        {activeTab === 'matched' ? (
                            <>
                                <h1 className="page-title">Your best sponsor opportunities</h1>
                                <p className="page-lead">
                                    Based on your newsletter profile
                                </p>
                                {hasOnboarding && matchedSponsors.length > 0 && (
                                    <p className="sponsors-matched-summary">
                                        {sponsors} sponsors · {matchSummary.strong} strong match{matchSummary.strong === 1 ? '' : 'es'} · {matchSummary.recentlyActive} recently active
                                    </p>
                                )}
                            </>
                        ) : (
                            <>
                                <h1 className="page-title">Sponsors</h1>
                                <p className="page-lead">
                                    Search {sponsors} companies, then contact their partnership email.
                                </p>
                            </>
                        )}
                    </header>

                    {error && (
                        <div className="alert alert--error" role="alert">
                            <FontAwesomeIcon icon={faInfoCircle} />
                            <span>{error}</span>
                            <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                    )}

                    {/* Tab Navigation */}
                    <div className="sponsors-tabs">
                        <button 
                            className={`sponsors-tab ${activeTab === 'all' ? 'active' : ''}`}
                            onClick={() => {
                                setActiveTab('all');
                                setShowLockedTabPrompt(false);
                            }}
                        >
                            <FontAwesomeIcon icon={faDatabase} />
                            All Sponsors
                        </button>
                        <button
                            type="button"
                            className={`sponsors-tab ${activeTab === 'matched' ? 'active' : ''} ${!hasOnboarding ? 'locked' : ''}`}
                            data-tooltip={!hasOnboarding ? 'Complete your newsletter profile to unlock AI-powered sponsor matching' : undefined}
                            onClick={() => {
                                if (hasOnboarding) {
                                    setActiveTab('matched');
                                    setShowLockedTabPrompt(false);
                                } else {
                                    setShowLockedTabPrompt(true);
                                }
                            }}
                            aria-describedby={!hasOnboarding && showLockedTabPrompt ? 'best-matches-locked-prompt' : undefined}
                        >
                            <FontAwesomeIcon icon={activeTab === 'matched' && hasOnboarding ? faCheckCircle : faDatabase} />
                            Best Matches
                            {!hasOnboarding && <FontAwesomeIcon icon={faLock} className="lock-icon" />}
                        </button>
                    </div>

                    {showLockedTabPrompt && !hasOnboarding && (
                        <div
                            id="best-matches-locked-prompt"
                            className="sponsors-tab-locked-prompt"
                            role="status"
                        >
                            <p className="sponsors-tab-locked-prompt__text">
                                To see your best sponsor matches, complete your newsletter profile so our AI can match you with the right sponsors.
                            </p>
                            <Link to="/onboarding" className="btn btn-primary sponsors-tab-locked-prompt__btn">
                                Complete Profile →
                            </Link>
                        </div>
                    )}

                    {/* Same message as the click-triggered prompt above, just
                        shown by default on All Sponsors instead of waiting for
                        a click on the locked tab — suppressed while that one's
                        already visible so the two never stack. */}
                    {!hasOnboarding && activeTab === 'all' && !showLockedTabPrompt && (
                        <div className="sponsors-onboarding-cta" role="status">
                            <FontAwesomeIcon icon={faInfoCircle} className="sponsors-onboarding-cta__icon" />
                            <p className="sponsors-onboarding-cta__text">
                                Complete your newsletter profile to unlock personalized Best Matches.
                            </p>
                            <Link to="/onboarding" className="btn btn-primary sponsors-onboarding-cta__btn">
                                Complete Profile →
                            </Link>
                        </div>
                    )}

                    {activeTab === 'all' && (
                    <div className="sponsors-controls-section">
                        <div className="sponsors-controls-header">
                            <h3>Find Your Perfect Sponsors</h3>
                            <select
                                className="audience-filter-select"
                                value={audienceFilter}
                                onChange={(e) => setAudienceFilter(e.target.value)}
                                title="Show sponsors that have sponsored newsletters of this size"
                            >
                                <option value="any">Any newsletter size</option>
                                <option value="under10k">Sponsors newsletters under 10k</option>
                                <option value="10k-50k">Sponsors newsletters 10k–50k</option>
                                <option value="50k-250k">Sponsors newsletters 50k–250k</option>
                                <option value="250k+">Sponsors newsletters 250k+</option>
                            </select>
                            <div className="sponsors-controls-actions">
                                {activeFilters.length > 0 && (
                                    <button className="clear-filters-btn" onClick={clearAllFilters}>
                                        <FontAwesomeIcon icon={faTimes} />
                                        Clear All ({activeFilters.length})
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        {(() => {
                            const audienceSize = user?.newsletterInfo?.audience_size || 0;
                            if (!audienceSize || rateGuidanceDismissed) return null;
                            const low = Math.round(audienceSize / 1000 * 20);
                            const high = Math.round(audienceSize / 1000 * 40);
                            return (
                                <div className="rate-guidance">
                                    <p className="rate-guidance__text">
                                        Newsletters your size ({audienceSize.toLocaleString()} readers) typically
                                        charge ${low.toLocaleString()}–${high.toLocaleString()} per placement ($20–40 CPM).
                                    </p>
                                    <button
                                        type="button"
                                        className="rate-guidance__dismiss"
                                        onClick={dismissRateGuidance}
                                        aria-label="Dismiss"
                                        title="Dismiss"
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                            );
                        })()}

                        <div className="sponsors-controls-content">
                            {/* Search Row */}
                            <div className="search-section">
                                <div className="search-box">
                                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                    <input
                                        id="sponsor-search"
                                        type="search"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search by name or niche"
                                        className="search-input"
                                        autoComplete="off"
                                    />
                                    {searchQuery && (
                                        <button 
                                            className="search-clear-btn"
                                            onClick={() => setSearchQuery('')}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            
                            {/* Filters Row — real niches present in the database, not a
                                fixed list, so a niche only shows up once there's
                                actually something to find in it. Collapsed by
                                default on mobile (see filtersExpanded) — desktop
                                shows the grid open regardless via CSS. */}
                            <div className="filters-section">
                                <button
                                    type="button"
                                    className="filters-toggle"
                                    onClick={() => setFiltersExpanded(v => !v)}
                                    aria-expanded={filtersExpanded}
                                >
                                    <FontAwesomeIcon icon={faSliders} />
                                    Filters
                                    {activeFilters.length > 0 && (
                                        <span className="filters-toggle__badge">{activeFilters.length}</span>
                                    )}
                                    <FontAwesomeIcon
                                        icon={faChevronDown}
                                        className={`filters-toggle__chevron ${filtersExpanded ? 'filters-toggle__chevron--open' : ''}`}
                                    />
                                </button>
                                <div className={`filters-grid-wrap ${filtersExpanded ? 'filters-grid-wrap--expanded' : ''}`}>
                                    <div className="filters-grid">
                                        {niches.map((niche) => (
                                            <button
                                                key={niche.tag}
                                                className={`filter-btn ${isFilterActive(niche.tag) ? 'active' : ''}`}
                                                onClick={() => handleFilterToggle(niche.tag)}
                                            >
                                                <FontAwesomeIcon icon={getNicheIcon(niche.tag)} />
                                                <span>{niche.tag}</span>
                                                {isFilterActive(niche.tag) && (
                                                    <FontAwesomeIcon icon={faCheckCircle} />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Status filter hidden in user-facing UI */}

                            {/* Sorting Row */}
                            <div className="sorting-section">
                                <span className="sorting-section__label">Sort by</span>
                                <div className="sorting-options">
                                    <button 
                                        className={`sort-btn ${sortBy === 'dateAdded' ? 'active' : ''}`}
                                        onClick={() => handleSort('dateAdded')}
                                    >
                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                        Date Added
                                        <FontAwesomeIcon 
                                            icon={sortBy === 'dateAdded' ? (sortOrder === 'asc' ? faArrowTrendUp : faArrowTrendDown) : faSort} 
                                        />
                                    </button>
                                    <button 
                                        className={`sort-btn ${sortBy === 'subscriberCount' ? 'active' : ''}`}
                                        onClick={() => handleSort('subscriberCount')}
                                    >
                                        <FontAwesomeIcon icon={faUsers} />
                                        Audience Size
                                        <FontAwesomeIcon 
                                            icon={sortBy === 'subscriberCount' ? (sortOrder === 'asc' ? faArrowTrendUp : faArrowTrendDown) : faSort} 
                                        />
                                    </button>
                                    <button 
                                        className={`sort-btn ${sortBy === 'sponsorName' ? 'active' : ''}`}
                                        onClick={() => handleSort('sponsorName')}
                                    >
                                        <FontAwesomeIcon icon={faBuilding} />
                                        Name
                                        <FontAwesomeIcon 
                                            icon={sortBy === 'sponsorName' ? (sortOrder === 'asc' ? faArrowTrendUp : faArrowTrendDown) : faSort} 
                                        />
                                    </button>
                                </div>
                            </div>
                            
                            {/* Affiliate Programs Toggle */}
                            <div className="affiliate-toggle-section">
                                <div className="affiliate-toggle-container">
                                    <label className="affiliate-toggle-label">
                                        <input
                                            type="checkbox"
                                            checked={showAffiliatePrograms}
                                            onChange={(e) => setShowAffiliatePrograms(e.target.checked)}
                                            className="affiliate-toggle-input"
                                        />
                                        <span className="affiliate-toggle-slider"></span>
                                        <span className="affiliate-toggle-text">
                                            Show Affiliate Programs
                                        </span>
                                    </label>
                                    <div className="affiliate-toggle-info">
                                        <FontAwesomeIcon icon={faInfoCircle} />
                                        <span>Affiliate programs pay commissions instead of direct sponsorships</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Matched Sponsors Section */}
                    {activeTab === 'matched' && (
                        <div className="sponsors-matched-section">
                            {!hasOnboarding ? (
                                <div className="sponsors-matched-locked">
                                    <FontAwesomeIcon icon={faDatabase} className="locked-icon" />
                                    <h3>Unlock Personalized Sponsor Matches</h3>
                                    <p>
                                        Complete your newsletter onboarding to see sponsors that match your newsletter's topic and audience.
                                    </p>
                                    <a href="/signup-flow/" className="btn btn-primary">
                                        Complete Onboarding
                                    </a>
                                </div>
                            ) : (
                                <>
                                    {loadingMatches ? (
                                        <div className="sponsors-matched-loading">
                                            <FontAwesomeIcon icon={faClock} spin />
                                            <span>Finding your matches...</span>
                                        </div>
                                    ) : matchedSponsors.length === 0 ? (
                                        <div className="sponsors-matched-empty">
                                            <FontAwesomeIcon icon={faInfoCircle} />
                                            <p>No matches found. Try updating your newsletter profile or browse all sponsors.</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Working a pipeline, not scrolling a database — these narrow
                                                the same fetched list client-side, no refetch needed. */}
                                            <div className="sponsors-match-filters">
                                                {([
                                                    ['all', 'All'],
                                                    ['strong', 'Strong matches'],
                                                    ['recent', 'Recently active'],
                                                    ['similar-audience', 'Similar audience'],
                                                    ['new', 'New'],
                                                ] as const).map(([key, label]) => (
                                                    <button
                                                        key={key}
                                                        type="button"
                                                        className={`sponsors-match-filter-chip ${matchFilter === key ? 'active' : ''}`}
                                                        onClick={() => setMatchFilter(key)}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                            {filteredMatchedSponsors.length === 0 ? (
                                                <div className="sponsors-matched-empty">
                                                    <FontAwesomeIcon icon={faInfoCircle} />
                                                    <p>No matches under this filter yet.</p>
                                                    <button type="button" className="btn btn-primary" onClick={() => setMatchFilter('all')}>
                                                        Show all matches
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="sponsors-matched-content">
                                                    <PaidSponsorTable
                                                        onError={setError}
                                                        activeFilter=""
                                                        isAdmin={isAdmin}
                                                        searchQuery=""
                                                        sortBy="matchScore"
                                                        sortOrder="desc"
                                                        user={user}
                                                        showAffiliatePrograms={false}
                                                        matchedSponsors={filteredMatchedSponsors}
                                                        showOneTime={false}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* Professional Table Section */}
                    {activeTab === 'all' && (
                    <div className="sponsors-table-section panel">
                        {(searchQuery || activeFilters.length > 0) && (
                            <p className="results-count visually-muted">
                                {activeFilters.length > 0 ? activeFilters.join(', ') : 'All categories'}
                                {searchQuery ? ` · matching "${searchQuery}"` : ''}
                            </p>
                        )}
                        <div className="sponsors-table-content">
                            <PaidSponsorTable 
                                onError={setError} 
                                activeFilter={activeFilters.join(',')} 
                                audienceFilter={audienceFilter}
                                isAdmin={isAdmin} 
                                searchQuery={searchQuery}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                user={user}
                                showAffiliatePrograms={showAffiliatePrograms}
                            />
                        </div>
                    </div>
                    )}
                </div>
            </div>
        );
    } else {
        return (
            <div className="web-page">
                <div className="web-section web-section-dark mt-0">
                    <div className="web-section__container web-section-content">
                        <div style={{ margin: '0 auto', maxWidth: 1000 }}>
                            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                                <h2>Unlock the Sponsor Database</h2>
                                <p>Choose a plan to access {sponsors}+ companies actively sponsoring {newsletters}+ newsletters.</p>
                            </div>
                            {/* Reuse Pricing component for subscription */}
                            {/* Using dynamic import replacement to avoid circular deps */}
                            <div>
                                {/* Inline lightweight pricing UI */}
                                <div className="subscribe__card-cont authed_subcribe-cont">
                                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                                        <button className="btn" onClick={() => handlePurchase()}>Subscribe — $20/month</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
};

export default Sponsors;